/* eslint-disable react/no-unescaped-entities */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, FileText, Gift, ScrollText, Home,
  Building2, Split, Layers, Mail, MoreHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

/**
 * Document type selection page — the "improved" landing step after a user
 * decides to analyse a notarial document. It NO LONGER asks for family
 * relationship before knowing the document type (that question, if needed
 * at all, now happens inside the personalized analysis on the dashboard).
 *
 * Main flow does not require this page: the homepage CTAs go directly to
 * /upload. This page remains reachable at /start for legacy URLs and for
 * users who want to declare the document type up-front, in which case the
 * choice is passed to /upload via location.state.
 */
const DOC_TYPES = [
  { id: 'succession', label: 'Succession', description: 'Acte ou déclaration de succession', Icon: ScrollText },
  { id: 'donation_partage', label: 'Donation-partage', description: 'Donation entre vifs avec partage', Icon: Gift },
  { id: 'testament', label: 'Testament', description: 'Testament olographe, authentique, legs', Icon: FileText },
  { id: 'attestation_immo', label: 'Attestation immobilière', description: 'Attestation de propriété après succession', Icon: Home },
  { id: 'vente_immo', label: 'Vente immobilière', description: 'Acte authentique, compromis, promesse', Icon: Building2 },
  { id: 'partage', label: 'Partage', description: 'Acte ou état liquidatif de partage', Icon: Split },
  { id: 'sci', label: 'SCI', description: 'Statuts, cession de parts, assemblée', Icon: Layers },
  { id: 'indivision', label: 'Indivision', description: 'Convention, sortie ou gestion d\'indivision', Icon: Layers },
  { id: 'courrier', label: 'Courrier notarial', description: 'Lettre, demande de pièces, projet d\'acte', Icon: Mail },
  { id: 'autre', label: 'Autre document notarial', description: 'Procuration, donation simple, avis fiscal…', Icon: MoreHorizontal },
];

const ONBOARDING = {
  container: 'onboarding-container',
  optionCard: 'onboarding-option',
  nextButton: 'onboarding-next-button',
  backButton: 'onboarding-back-button',
};

const OnboardingPage = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);

  const handleContinue = () => {
    // Pass the chosen type to UploadPage via state. UploadPage may use
    // it as a hint; the AI engine still auto-detects from the file.
    navigate('/upload', {
      state: { onboarding: { documentType: selected } },
    });
  };

  return (
    <div
      data-testid={ONBOARDING.container}
      className="min-h-screen bg-[#F9F7F3]"
    >
      <SEO
        title="Choisir un type de document notarié | HÉRITA"
        description="Sélectionnez le type de document notarié à analyser : succession, donation-partage, testament, vente immobilière, attestation, partage, indivision, SCI ou courrier notarial."
        path="/start"
        noindex
      />
      {/* Top bar */}
      <div className="bg-white border-b border-[#E2DBCF] py-5 px-6 md:px-12">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#1A2530] hover:opacity-70 transition-opacity"
          >
            Hérita
          </button>
          <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.22em] text-[#2C363F] opacity-80">
            Choix du document
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="py-14 sm:py-20 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-10 sm:mb-12 text-center">
            <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#2C363F] opacity-70 mb-5">
              Étape 1 — Type de document
            </p>
            <h1
              className="font-['Cormorant_Garamond'] font-light text-[34px] sm:text-[44px] lg:text-[52px] text-[#1A2530] mb-4 leading-[1.15]"
              style={{ letterSpacing: '-0.01em' }}
            >
              Quel type de document souhaitez-vous analyser&nbsp;?
            </h1>
            <p className="font-['Work_Sans'] text-[15px] sm:text-base text-[#2C363F] max-w-xl mx-auto leading-relaxed">
              Choisissez le document correspondant afin d'adapter l'analyse à
              votre situation.
            </p>
          </div>

          {/* Cards grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
            {DOC_TYPES.map(({ id, label, description, Icon }) => {
              const isSelected = selected === id;
              return (
                <button
                  key={id}
                  type="button"
                  data-testid={`${ONBOARDING.optionCard}-${id}`}
                  onClick={() => setSelected(id)}
                  className={`group w-full text-left p-5 rounded-2xl border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-[#1A2530] bg-white shadow-md'
                      : 'border-[#E2DBCF] bg-white hover:border-[#D3CCBE] hover:bg-[#F9F7F3]'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected ? 'bg-[#1A2530] text-white' : 'bg-[#E3D5CA] text-[#1A2530]'
                      }`}
                    >
                      <Icon className="h-4.5 w-4.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-['Work_Sans'] font-semibold text-[15px] text-[#1A2530] mb-0.5 leading-tight">
                        {label}
                      </p>
                      <p className="font-['Work_Sans'] text-[12.5px] text-[#2C363F] leading-snug">
                        {description}
                      </p>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 flex-shrink-0 mt-1 ${
                        isSelected
                          ? 'border-[#1A2530] bg-[#1A2530]'
                          : 'border-[#E2DBCF] bg-white'
                      }`}
                    >
                      {isSelected && (
                        <Check className="h-3 w-3 text-white" strokeWidth={3} />
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between gap-4">
            <Button
              data-testid={ONBOARDING.backButton}
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-[#2C363F] hover:text-[#1A2530] hover:bg-transparent font-['Work_Sans'] -ml-2"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour à l'accueil
            </Button>

            <Button
              data-testid={ONBOARDING.nextButton}
              onClick={handleContinue}
              disabled={!selected}
              className={`px-8 py-6 rounded-full font-['Work_Sans'] font-medium text-base transition-all duration-300 ${
                selected
                  ? 'bg-[#1A2530] hover:bg-[#2C363F] text-white shadow-md hover:shadow-lg'
                  : 'bg-[#E2DBCF] text-[#2C363F] cursor-not-allowed opacity-60'
              }`}
            >
              Analyser mon document
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Skip link — direct upload without specifying a type */}
          <div className="text-center mt-6">
            <button
              type="button"
              onClick={() => navigate('/upload')}
              data-testid="onboarding-skip-link"
              className="font-['Work_Sans'] text-[13px] text-[#2C363F] hover:text-[#1A2530] underline underline-offset-[5px] decoration-[#D3CCBE] hover:decoration-[#1A2530] transition-colors"
            >
              Importer directement sans choisir le type
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
